import { useState, useEffect, useCallback } from "react";
import Layout from "../../../components/Layout";
import { StatusBadge } from "../../../components/StatusBadge";
import { HiTrendingUp, HiSearch, HiCheck, HiRefresh, HiDownload, HiExclamation } from "react-icons/hi";
import { CustomButton, CustomInput } from "../../../components/FormFields";
import { CustomTable, CustomTableHead, CustomTableBody, CustomTableHeadCell, CustomTableRow, CustomTableCell } from "../../../components/CustomTable";
import { fetchActiveEmployees } from "../../../services/employeeService";
import {
  fetchIncrements, seedIncrementSheet, clearIncrementDraft,
  updateIncrement, applyIncrements, mergeIncrementsIntoBasic,
} from "../../../services/incrementBonusService";
import { downloadCSV } from "../../../services/reportService";

const money = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = String(m).split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
};
// Increments normally take effect in September.
const defaultMonth = () => `${new Date().getFullYear()}-09`;

export default function IncrementSheet() {
  const [month, setMonth]     = useState(defaultMonth);
  const [rows, setRows]       = useState([]);
  const [search, setSearch]   = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState("");
  const [error, setError]     = useState("");
  const [savingId, setSaving] = useState(null);

  const applied = rows.length > 0 && rows.some((r) => r.status === "Applied");
  const merged  = rows.length > 0 && rows.every((r) => r.status === "Merged");
  const stage   = merged ? "Merged" : applied ? "Applied" : "Draft";

  const load = useCallback(async (m) => {
    setLoading(true); setError("");
    try {
      const d = await fetchIncrements(m);
      setRows(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e); setError("Could not load the increment sheet.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const handleGenerate = async () => {
    setBusy("Generating…"); setError("");
    try {
      const emps = await fetchActiveEmployees();
      const list = (Array.isArray(emps) ? emps : []).map((e) => ({
        emp_id: e.emp_id,
        empName: `${e.initial ?? ""} ${e.firstName ?? ""} ${e.surName ?? ""}`.replace(/\s+/g, " ").trim(),
        category: e.category ?? "",
        basicSalary: e.basicSalary,
      }));
      await clearIncrementDraft(month);
      await seedIncrementSheet(month, list);
      await load(month);
    } catch (e) {
      console.error(e); setError("Could not generate the sheet.");
    } finally { setBusy(""); }
  };

  const editAmount = (id, value) =>
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const amt = Number(value) || 0;
      return { ...r, incrementAmount: value, newBasic: Number(r.currentBasic || 0) + amt };
    }));

  const commitAmount = async (row) => {
    if (row.status !== "Draft") return;
    setSaving(row.id);
    try { await updateIncrement(row.id, Number(row.incrementAmount) || 0); }
    catch (e) { console.error(e); setError("Could not save that amount."); }
    finally { setSaving(null); }
  };

  const handleApply = async () => {
    const changed = rows.filter((r) => Number(r.incrementAmount) > 0).length;
    if (changed === 0) { setError("Enter at least one increment amount first."); return; }
    if (!window.confirm(
      `Apply ${changed} increment${changed === 1 ? "" : "s"} for ${monthLabel(month)}?\n\n` +
      `They will show as a separate Increment line on that month's payroll. ` +
      `Basic salary is not changed yet — that happens when you merge them.`
    )) return;
    setBusy("Applying…"); setError("");
    try { await applyIncrements(month); await load(month); }
    catch (e) { console.error(e); setError("Could not apply the increments."); }
    finally { setBusy(""); }
  };

  const handleMerge = async () => {
    if (!window.confirm(
      `Merge these increments into basic salary?

` +
      `Do this once ${monthLabel(month)} has been paid. From the next month the rise ` +
      `becomes part of basic and the separate Increment line disappears. This cannot be undone here.`
    )) return;
    setBusy("Merging…"); setError("");
    try { await mergeIncrementsIntoBasic(month); await load(month); }
    catch (e) { console.error(e); setError("Could not merge the increments."); }
    finally { setBusy(""); }
  };

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return !q || (r.empName || "").toLowerCase().includes(q) || String(r.emp_id).includes(q);
  });
  const withAmount = filtered.filter((r) => Number(r.incrementAmount) > 0);
  const totalIncrement = filtered.reduce((a, r) => a + (Number(r.incrementAmount) || 0), 0);

  return (
    <Layout>
      <div className="flex flex-col gap-6">

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Increment Sheet</h1>
            <p className="mt-1 text-sm text-gray-500">Shown as a separate line that month, then merged into basic salary</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200" />
            <StatusBadge color={merged ? "success" : applied ? "indigo" : "warning"}
              className="px-3 py-1.5 text-sm font-semibold">{stage}</StatusBadge>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <HiExclamation className="h-5 w-5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <CustomButton color="purple" disabled={!!busy || applied || merged} onClick={handleGenerate}>
            <HiRefresh className="mr-2 h-4 w-4" /> {rows.length ? "Regenerate Sheet" : "Generate Sheet"}
          </CustomButton>
          {!applied && !merged && rows.length > 0 && (
            <CustomButton color="indigo" disabled={!!busy} onClick={handleApply}>
              <HiCheck className="mr-2 h-4 w-4" /> Apply to {monthLabel(month)} Payroll
            </CustomButton>
          )}
          {applied && !merged && (
            <CustomButton color="success" disabled={!!busy} onClick={handleMerge}>
              <HiTrendingUp className="mr-2 h-4 w-4" /> Merge into Basic Salary
            </CustomButton>
          )}
          <CustomButton color="gray" outline disabled={rows.length === 0}
            onClick={() => downloadCSV(`increments-${month}.csv`, rows, [
              { key: "emp_id", label: "Emp ID" }, { key: "empName", label: "Employee" },
              { key: "category", label: "Category" }, { key: "currentBasic", label: "Current Basic" },
              { key: "incrementAmount", label: "Increment" }, { key: "newBasic", label: "New Basic" },
            ])}>
            <HiDownload className="mr-2 h-4 w-4" /> Export CSV
          </CustomButton>
          <CustomInput icon={HiSearch} placeholder="Search name or ID..." sizing="sm" className="w-56"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          {busy && <span className="text-sm text-gray-500">{busy}</span>}
        </div>

        {applied && !merged && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">
            Applied. {monthLabel(month)} payroll will show these as a separate <span className="font-semibold">Increment</span> line
            and basic salary is unchanged. Once that month is paid, use <span className="font-semibold">Merge into Basic Salary</span>.
          </div>
        )}
        {merged && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
            Merged — these amounts are now part of basic salary, so future payroll, EPF and ETF all use the higher figure.
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <CustomTable hoverable>
              <CustomTableHead>
                <CustomTableRow>
                  <CustomTableHeadCell>Employee</CustomTableHeadCell>
                  <CustomTableHeadCell>Category</CustomTableHeadCell>
                  <CustomTableHeadCell>Current Basic</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-amber-600">Increment ✎</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-green-600">New Basic</CustomTableHeadCell>
                  <CustomTableHeadCell>Status</CustomTableHeadCell>
                </CustomTableRow>
              </CustomTableHead>
              <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <CustomTableRow><CustomTableCell colSpan={6} className="py-10 text-center text-sm text-gray-400">Loading…</CustomTableCell></CustomTableRow>
                ) : filtered.length === 0 ? (
                  <CustomTableRow><CustomTableCell colSpan={6} className="py-10 text-center text-sm text-gray-400">
                    No sheet for {monthLabel(month)} yet — click <span className="font-semibold">Generate Sheet</span>.
                  </CustomTableCell></CustomTableRow>
                ) : filtered.map((r) => (
                  <CustomTableRow key={r.id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                    <CustomTableCell>
                      <p className="whitespace-nowrap text-sm font-medium text-gray-800 dark:text-gray-200">{r.empName}</p>
                      <p className="font-mono text-xs text-purple-500">{r.emp_id}</p>
                    </CustomTableCell>
                    <CustomTableCell className="whitespace-nowrap text-xs text-gray-500">{r.category || "—"}</CustomTableCell>
                    <CustomTableCell className="text-sm text-gray-700 dark:text-gray-300">{money(r.currentBasic)}</CustomTableCell>
                    <CustomTableCell>
                      {r.status === "Draft" ? (
                        <input type="number" step="0.01" min="0" disabled={savingId === r.id}
                          value={r.incrementAmount ?? 0}
                          onChange={(e) => editAmount(r.id, e.target.value)}
                          onBlur={() => commitAmount(r)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          className="w-28 rounded border border-gray-300 bg-amber-50 px-2 py-1 text-sm text-gray-800 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
                      ) : (
                        <span className="text-sm text-gray-600">{money(r.incrementAmount)}</span>
                      )}
                    </CustomTableCell>
                    <CustomTableCell className="whitespace-nowrap text-sm font-bold text-green-600">{money(r.newBasic)}</CustomTableCell>
                    <CustomTableCell>
                      <StatusBadge color={r.status === "Merged" ? "success" : r.status === "Applied" ? "indigo" : "warning"}
                        className="w-fit text-xs">{r.status}</StatusBadge>
                    </CustomTableCell>
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            </CustomTable>
          </div>

          {filtered.length > 0 && (
            <div className="grid grid-cols-2 gap-4 border-t border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/50 sm:grid-cols-3">
              <div>
                <p className="text-xs text-gray-500">Employees on sheet</p>
                <p className="text-lg font-bold text-gray-700 dark:text-gray-200">{filtered.length}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Receiving an increment</p>
                <p className="text-lg font-bold text-purple-600">{withAmount.length}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total monthly increase</p>
                <p className="text-lg font-bold text-green-600">Rs. {money(totalIncrement)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          <HiTrendingUp className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Leave an employee at 0 to skip them — only non-zero rows are applied.
            Apply first so the increment shows as its own line on that month's payslip, then merge it into
            basic salary once the month has been paid.
          </span>
        </div>
      </div>
    </Layout>
  );
}
